import useShowPropertyType from "@hooks/useShowPropertyType";
import { getCustomExtraPathMainDashboard } from "@pages/Dashboard/helper";
import services from "@src/Services";
import { getPropertyCustomExtraPath } from "@src/constants/Dashboard";
import dayjs from "dayjs";

import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const initialDropDownOptions = [
  { label: "By Real-Time", value: "real" },
  { label: "By Hour", value: "hour" },
  { label: "By Daypart", value: "daypart" },
];

export const TOTAL_SALES_VIEW = {
  REAL_TIME: "By Real-Time",
  HOUR: "By Hour",
  DAY_PART: "By Daypart",
};

export const TOTAL_SALES_VIEW_VALUE = {
  [TOTAL_SALES_VIEW.REAL_TIME]: "real",
  [TOTAL_SALES_VIEW.HOUR]: "hour",
  [TOTAL_SALES_VIEW.DAY_PART]: "daypart",
};

export const getCustomExtraPath = (filters: RevenueFilterType) => {
  let path = "";
  const { filterBy } = filters || {};

  const customPath = getPropertyCustomExtraPath(filters);

  path = customPath;

  if (filterBy) {
    path += `&filter_by=${filterBy?.toString()}`;
  }

  let url = path;
  if (path.length && path[0] !== "?") {
    url = url.replace("&", "?");
  }
  return url;
};

export const getTimeMapped = (
  mapData: RevenueGraphDataType[],
  activeTab: string
) => {
  const data = mapData?.map((i: RevenueGraphDataType) => {
    if (activeTab === TOTAL_SALES_VIEW.DAY_PART && i?.game_status_name)
      return i?.game_status_name;
    return (
      i?.datetime && dayjs(i?.datetime).tz("America/New_York").format("HH:mm")
    );
  });
  return data;
};

export const getSalesMapped = (
  mapData: RevenueGraphDataType[],
  activeTab: string
) => {
  const data = mapData?.map((i: RevenueGraphDataType) => {
    if (activeTab === TOTAL_SALES_VIEW.DAY_PART && i?.revenue)
      return i?.revenue;
    return i?.sales;
  });
  return data;
};

export const getRevenueGraphData = async (filters: RevenueFilterType) => {
  try {
    const advancedFilterExtraPath = getCustomExtraPath({ ...filters });

    const customPath = `${advancedFilterExtraPath}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await services.get(`/revenue-time-api${customPath}`);

    if (!res?.error) {
      return { data: res?.data?.data };
    }
    return { data: null, error: "Something went wrong !" };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    return {
      error: err?.error?.message || "Something went wrong!",
    };
  }
};

export const getRevenueData = async (filters: DashboardFilterType) => {
  try {
    const advancedFilterExtraPath = getCustomExtraPathMainDashboard({
      ...filters,
    });

    const customPath = `${advancedFilterExtraPath}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await services.get(`/revenue-api${customPath}`);

    if (!res?.error) {
      return { data: res?.data?.data };
    }
    return { data: null, error: "Something went wrong!" };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    return {
      error: err?.error?.message || "Something went wrong!",
    };
  }
};

export const getRevenueTotalSalesMapData = (
  mappedData: RevenueTotalSalesDataType,
  filter: number
) => {
  const hideStilePerCap = useShowPropertyType(filter, "hideStilePerCap");
  const hideGuestData = useShowPropertyType(filter, "hideGuestData");
  const hideGrossMargin = useShowPropertyType(filter, "hideGrossMargin");
  const isQsPropertyStore = useShowPropertyType(filter, "isQsPropertyStore");

  const propertyRestaurantCateringRevenueCenters = useShowPropertyType(
    filter,
    "propertyRestaurantCateringRevenueCenters"
  );

  const quickServiceRevenueCenters = useShowPropertyType(
    filter,
    "quickServiceRevenueCenters"
  );

  let currentTarget: CurrentTargetAvgType[] = [
    {
      title: "current",
      value: mappedData?.currentSales,
      isCurrent: true,
      formatWithDollar: true,
    },
  ];

  const revenueTarget = {
    title: "Target",
    value: mappedData?.targetSales,
    formatWithDollar: true,
    isTarget: true,
  };

  // hide target
  if (
    !(propertyRestaurantCateringRevenueCenters || quickServiceRevenueCenters)
  ) {
    currentTarget = [...currentTarget, revenueTarget];
  }

  const YtdAvg = {
    title: "YTD Avg",
    value: mappedData?.ytdRevenueAvg,
    formatWithDollar: true,
  };

  currentTarget = [...currentTarget, YtdAvg];

  const netStile = {
    title: "Net Stile",
    value: mappedData?.stile?.net,
    formatWithComma: true,
  };

  const grossStile = {
    title: "Gross Stile",
    value: mappedData?.stile?.gross,
    formatWithComma: true,
  };

  const sold = {
    title: "% of Plan",
    value: mappedData?.stile?.sold_pct,
    formatPercentage: true,
  };

  const stile = isQsPropertyStore
    ? [netStile, sold]
    : [netStile, grossStile, sold];

  const netPerCap = {
    title: "Net Per Cap",
    value: mappedData?.perCap?.net,
    formatWithDollar: true,
  };

  const grossPerCap = {
    title: "Gross Per Cap",
    value: mappedData?.perCap?.gross,
    formatWithDollar: true,
  };

  const plan = {
    title: "Plan",
    value: mappedData?.perCap?.plan,
    formatWithDollar: true,
  };

  const perCap = isQsPropertyStore
    ? [netPerCap, plan]
    : [netPerCap, grossPerCap, plan];

  const transaction = [
    {
      title: "Total Tr",
      value: mappedData?.transaction?.total,
      formatWithComma: true,
    },
    {
      title: "Avg $ / Tr",
      value: mappedData?.transaction?.avg,
      formatWithDollar: true,
    },
    {
      title: "YTD $ / Tr",
      value: mappedData?.transaction?.ytd_avg,
      formatWithDollar: true,
    },
  ];

  const grossMargin = [
    {
      title: "Gross Margin",
      value: mappedData?.grossMargin?.pct,
      formatPercentage: true,
    },
    {
      title: "",
      value: mappedData?.grossMargin?.act,
      formatWithDollar: true,
    },
  ];

  let guestCountCompression: GuestCountCompressionType[] = [
    {
      title: "Guest Count",
      value: mappedData?.guest?.count,
      formatWithComma: true,
    },
    {
      title: "Avg $ / Guest",
      value: mappedData?.guest?.avg,
      formatWithDollar: true,
    },
  ];

  const compression = {
    title: "Compression",
    value: mappedData?.compression,
    formatPercentage: true,
  };

  let dataList: TotalSalesDataType[] = [
    {
      id: Math.random(),
      mapDataRevenue: currentTarget,
    },
  ];

  if (propertyRestaurantCateringRevenueCenters) {
    guestCountCompression = [...guestCountCompression, compression];
  }

  if (!hideStilePerCap) {
    dataList = [
      ...dataList,
      ...[
        {
          id: Math.random(),
          mapDataRevenue: stile,
        },
        {
          id: Math.random(),
          mapDataRevenue: perCap,
        },
      ],
    ];
  }

  if (!hideGuestData) {
    dataList = [
      ...dataList,
      ...[
        {
          id: Math.random(),
          mapDataRevenue: guestCountCompression,
        },
      ],
    ];
  }

  // transaction
  dataList = [
    ...dataList,
    ...[
      {
        id: Math.random(),
        mapDataRevenue: transaction,
      },
    ],
  ];

  if (!hideGrossMargin) {
    dataList = [
      ...dataList,
      ...[
        {
          id: Math.random(),
          mapDataRevenue: grossMargin,
        },
      ],
    ];
  }

  return dataList;
};

export const initialRevenueTotalSalesData = {
  targetValue: null,
  lastUpdated: "",
  eventName: "",
};

export const initialRevenueDashboardData: RevenueTotalSalesDataType = {
  event_name: "",
  propertyType: 0,
  currentSales: null,
  targetSales: null,
  ytdRevenueAvg: null,
  stile: {
    net: null,
    gross: null,
    sold_pct: null,
  },
  perCap: {
    net: null,
    gross: null,
    plan: null,
  },
  transaction: {
    total: null,
    avg: null,
    ytd_avg: null,
  },
  grossMargin: {
    pct: null,
    act: null,
  },
  guest: {
    count: null,
    avg: null,
  },
  compression: null,
};
